import { QueryClient, QueryKey } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { Entity, EntityPreviewAsset } from "@/queries/folder-entities";
import { Response } from "@/queries/types";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface EntityPreviewUpdateMessage {
  workspace_id: number;
  entity_type: string;
  entity_id: string;
  status: EntityPreviewAsset["status"];
  entity_version_key?: string | null;
  content_hash?: string | null;
  generated_at?: string | null;
  error_message?: string | null;
  image_url?: string | null;
}

interface LookupEntityPreviewResponse {
  success: boolean;
  previews: {
    entity_type: string;
    entity_id: string;
    preview?: EntityPreviewAsset | null;
  }[];
  message?: string;
}

type FolderEntitiesQueryData = Response<Entity[], "entities", string>;

export const entityPreviewQueryKey = (
  workspaceId: number,
  entityType: string,
  entityId: number | string,
) => ["entity-preview", String(workspaceId), entityType, String(entityId)] as const;

export const isEntityPreviewUpdateMessage = (
  value: unknown,
): value is EntityPreviewUpdateMessage => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<EntityPreviewUpdateMessage>;
  return (
    typeof candidate.workspace_id === "number" &&
    typeof candidate.entity_type === "string" &&
    typeof candidate.entity_id === "string" &&
    (candidate.status === "pending" ||
      candidate.status === "ready" ||
      candidate.status === "failed")
  );
};

const isFolderEntitiesQuery = (queryKey: QueryKey, workspaceId: number): boolean => {
  if (queryKey[0] !== "folder-entities") {
    return false;
  }

  return queryKey[1] === String(workspaceId);
};

const folderEntitiesQueryPredicate = (workspaceId: number) => (query: { queryKey: QueryKey }) =>
  isFolderEntitiesQuery(query.queryKey, workspaceId);

export const reducePreviewState = (
  currentPreview: EntityPreviewAsset | undefined,
  update: EntityPreviewUpdateMessage,
): EntityPreviewAsset => {
  const updateContentHash = update.content_hash ?? undefined;
  const updateVersionKey = update.entity_version_key ?? undefined;
  const hasCurrentIdentity = Boolean(
    currentPreview?.content_hash || currentPreview?.entity_version_key,
  );
  const hasUpdateIdentity = Boolean(updateContentHash || updateVersionKey);
  const hasIdentityConflict =
    hasCurrentIdentity &&
    hasUpdateIdentity &&
    Boolean(currentPreview?.content_hash) &&
    Boolean(updateContentHash) &&
    currentPreview?.content_hash !== updateContentHash;

  if (update.status === "pending") {
    if (hasIdentityConflict) {
      return {
        status: "pending",
        content_hash: updateContentHash,
        entity_version_key: updateVersionKey,
        is_regenerating: true,
      };
    }

    if (currentPreview?.image_url) {
      return {
        ...currentPreview,
        status: "ready",
        content_hash: updateContentHash ?? currentPreview.content_hash,
        entity_version_key: updateVersionKey ?? currentPreview.entity_version_key,
        is_regenerating: true,
      };
    }

    return {
      status: "pending",
      content_hash: updateContentHash,
      entity_version_key: updateVersionKey,
      is_regenerating: true,
    };
  }

  // Guard against late successful deliveries from an older generation.
  if (hasIdentityConflict && update.status === "ready") {
    return currentPreview ?? { status: "pending", is_regenerating: true };
  }

  if (update.status === "ready") {
    if (update.image_url) {
      return {
        status: "ready",
        image_url: update.image_url,
        generated_at: update.generated_at ?? undefined,
        content_hash: updateContentHash ?? currentPreview?.content_hash,
        entity_version_key: updateVersionKey ?? currentPreview?.entity_version_key,
        is_regenerating: false,
      };
    }

    if (currentPreview?.image_url) {
      return {
        ...currentPreview,
        status: "ready",
        content_hash: updateContentHash ?? currentPreview.content_hash,
        entity_version_key: updateVersionKey ?? currentPreview.entity_version_key,
        is_regenerating: false,
      };
    }

    return {
      status: "pending",
      generated_at: update.generated_at ?? undefined,
      content_hash: updateContentHash,
      entity_version_key: updateVersionKey,
      is_regenerating: true,
    };
  }

  if (currentPreview?.image_url && !hasIdentityConflict) {
    return {
      ...currentPreview,
      content_hash: updateContentHash ?? currentPreview.content_hash,
      entity_version_key: updateVersionKey ?? currentPreview.entity_version_key,
      is_regenerating: false,
    };
  }

  return {
    status: "failed",
    generated_at: update.generated_at ?? undefined,
    content_hash: updateContentHash,
    entity_version_key: updateVersionKey,
    is_regenerating: false,
  };
};

export const mergeInitialPreviewState = (
  currentPreview: EntityPreviewAsset | undefined,
  initialPreview: EntityPreviewAsset | undefined,
): EntityPreviewAsset | undefined => {
  if (!initialPreview) {
    return currentPreview;
  }

  if (!currentPreview) {
    return initialPreview;
  }

  if (initialPreview.image_url && initialPreview.image_url !== currentPreview.image_url) {
    return initialPreview;
  }

  if (currentPreview.status === "pending" && initialPreview.status !== "pending") {
    return initialPreview;
  }

  if (currentPreview.status === "failed" && initialPreview.status === "ready") {
    return initialPreview;
  }

  return currentPreview;
};

export const applyEntityPreviewUpdate = (
  queryClient: QueryClient,
  update: EntityPreviewUpdateMessage,
): void => {
  queryClient.setQueryData<EntityPreviewAsset | undefined>(
    entityPreviewQueryKey(update.workspace_id, update.entity_type, update.entity_id),
    currentPreview => reducePreviewState(currentPreview, update),
  );

  queryClient.setQueriesData<FolderEntitiesQueryData>(
    {
      predicate: folderEntitiesQueryPredicate(update.workspace_id),
    },
    currentData => {
      if (!currentData?.entities) {
        return currentData;
      }

      let didUpdate = false;
      const entities = currentData.entities.map(entity => {
        if (entity.type !== update.entity_type || String(entity.id) !== update.entity_id) {
          return entity;
        }

        didUpdate = true;
        return {
          ...entity,
          preview: reducePreviewState(entity.preview, update),
        };
      });

      return didUpdate ? { ...currentData, entities } : currentData;
    },
  );
};

export const applyEntityPreviewAsset = (
  queryClient: QueryClient,
  params: {
    workspaceId: number;
    entityType: string;
    entityId: string;
    preview: EntityPreviewAsset;
  },
): void => {
  applyEntityPreviewUpdate(queryClient, {
    workspace_id: params.workspaceId,
    entity_type: params.entityType,
    entity_id: params.entityId,
    status: params.preview.status,
    entity_version_key: params.preview.entity_version_key ?? null,
    content_hash: params.preview.content_hash ?? null,
    generated_at: params.preview.generated_at ?? null,
    image_url: params.preview.image_url ?? null,
  });
};

export const lookupEntityPreviewAsset = async ({
  workspaceId,
  entityType,
  entityId,
  userToken,
  requestMissing = false,
}: {
  workspaceId: number;
  entityType: string;
  entityId: string;
  userToken: string;
  requestMissing?: boolean;
}): Promise<EntityPreviewAsset | undefined> => {
  const response = await fetch(ENDPOINTS.entity_previews.lookup(workspaceId), {
    method: "POST",
    headers: jsonAuthHeaders(userToken),
    body: JSON.stringify({
      refs: [
        {
          entity_type: entityType,
          entity_id: entityId,
        },
      ],
      request_missing: requestMissing,
    }),
  });

  const result = (await handleResponse(response)) as LookupEntityPreviewResponse;
  const matchingPreview = result.previews.find(
    preview => preview.entity_type === entityType && preview.entity_id === entityId,
  );

  return matchingPreview?.preview ?? undefined;
};
